import { TrackingEvents } from './events';

const PAYMENT_PAYLOAD_KEYS = new Set([
  'value',
  'currency',
  'selected_product',
  'item_id',
  'item_name',
  'eventId',
  'event_id',
  'transaction_id',
  'items',
]);

const internalEvent = (eventName, payload, options = {}) => ({
  eventName,
  payload,
  options,
});

const buildTransaction = (payload, overrides = {}) => {
  const transaction = {
    revenue:
      overrides.revenue ??
      payload.value ??
      payload.price ??
      payload.selected_product?.prices?.[0]?.amount,
    currency:
      overrides.currency ?? payload.currency ?? payload.selected_product?.prices?.[0]?.currency,
    product_sku:
      overrides.product_sku ?? payload.selected_product?.sku ?? payload.item_name ?? payload.item_id,
    product_name: overrides.product_name ?? payload.selected_product?.name ?? payload.item_name,
  };

  return Object.fromEntries(
    Object.entries(transaction).filter(([, value]) => value != null && value !== '')
  );
};

const internalPaymentEvent = (eventName, payload, transactionOverrides = {}) => {
  const transaction = buildTransaction(payload, transactionOverrides);
  const properties = Object.fromEntries(
    Object.entries(payload).filter(([key]) => !PAYMENT_PAYLOAD_KEYS.has(key))
  );

  return internalEvent(eventName, {
    ...properties,
    ...(Object.keys(transaction).length ? { transaction } : {}),
  });
};

/**
 * Maps canonical app events to provider-specific names and payloads.
 */
export const mapEventForProviders = (eventKey, payload = {}) => {
  const base = {
    google: { eventName: null, payload: {} },
    facebook: { eventName: null, payload: {} },
    tiktok: { eventName: null, payload: {} },
    internal: { eventName: null, payload: {} },
  };

  switch (eventKey) {
    case TrackingEvents.PAGE_VIEW:
      return {
        ...base,
        google: { eventName: 'navigate_page', payload },
        facebook: { eventName: 'PageView', payload }
      };

    case TrackingEvents.QUIZ_STARTED:
      return {
        ...base,
        facebook: { eventName: 'QuizStarted', payload, options: { isCustom: true } },
        internal: internalEvent('quiz_started', payload),
      };

    case TrackingEvents.QUIZ_COMPLETED:
      return {
        ...base,
        facebook: { eventName: 'QuizCompleted', payload, options: { isCustom: true } },
        internal: internalEvent('quiz_completed', payload),
      };

    case TrackingEvents.LEAD_CREATED:
      return {
        ...base,
        facebook: { eventName: 'Lead', payload },
        tiktok: {
          eventName: 'Lead',
          payload: { email: payload.email, external_id: payload.lead_code },
        },
        internal: internalEvent('lead_created', payload),
      };

    case TrackingEvents.CHECKOUT_STARTED:
      return {
        ...base,
        facebook: { eventName: 'InitiateCheckout', payload },
        tiktok: {
          eventName: 'InitiateCheckout',
          payload: {
            contents: [
              {
                num_items: 3,
              },
            ],
          },
        },
        internal: internalPaymentEvent('summary_cta_clicked', payload),
      };

    case TrackingEvents.PAYMENT_INFO_ADDED:
      return {
        ...base,
        facebook: { eventName: 'AddPaymentInfo', payload },
        tiktok: {
          eventName: 'AddToCart',
          payload: {
            contents: [
              {
                content_id: payload.item_id,
                content_name: payload.item_name,
                price: payload.price,
              },
            ],
            value: payload.price,
            currency: payload.currency,
          },
        },
        internal: internalPaymentEvent('checkout_cta_clicked', payload),
      };

    case TrackingEvents.PAYMENT_ATTEMPTED:
      return {
        ...base,
        facebook: { eventName: 'payment_attempted', payload },
        google: { eventName: 'payment_attempted', payload },
        internal: internalPaymentEvent('payment_attempted', payload),
      };

    case TrackingEvents.PURCHASE_COMPLETED:
      return {
        ...base,
        google: { eventName: 'purchase', payload },
        facebook: { eventName: 'Purchase', payload },
        tiktok: {
          eventName: 'Purchase',
          payload: {
            contents: [
              {
                content_id: payload.item_id,
                content_name: payload.item_name,
                price: payload.value,
              },
            ],
            value: payload.value,
            currency: payload.currency,
          },
          options: {
            eventId: payload.eventId || payload.event_id,
            beOptions: {
              code: payload.lead_code,
              email: payload.email,
              product_sku: payload.selected_product?.sku || payload.item_name,
              currency: payload.currency || payload.selected_product?.prices?.[0]?.currency,
              amount: payload.value ?? payload.selected_product?.prices?.[0]?.amount,
              event_id: payload.eventId || payload.event_id,
            },
          },
        },
        internal: internalPaymentEvent('purchase_completed', payload),
      };

    case TrackingEvents.PURCHASE_UPSELL:
      return {
        ...base,
        facebook: { eventName: 'PurchaseUpsell', payload, options: { isCustom: true } },
        internal: internalPaymentEvent('purchase_upsell', payload),
      };

    case TrackingEvents.TRUSTPILOT_REVIEW_OPENED:
      return {
        ...base,
        google: { eventName: 'click_tp_review_open', payload },
        internal: internalEvent('trustpilot_review_opened', payload),
      };

    default:
      return {
        ...base,
        google: { eventName: eventKey, payload },
        facebook: { eventName: eventKey, payload },
        tiktok: { eventName: eventKey, payload },
        internal: internalEvent(eventKey.toLowerCase(), payload),
      };
  }
};
